import React, { useEffect, useState } from 'react';
import emailjs from '@emailjs/browser';

const SERVICE_ID = 'hirareml-zoho';
const TEMPLATE_ID = 'rareml-contact-template';
const PUBLIC_KEY = '83w7DYVocZoV0DGiq';
const RAREML_FROM_EMAIL = 'hi@rareml.ai';

const fieldStyle = {
  width: '100%',
  padding: '14px 16px',
  borderRadius: 16,
  border: '1px solid rgba(169, 183, 208, 0.22)',
  background: 'rgba(255,255,255,0.05)',
  color: '#f5f7fb',
  outline: 'none'
};

const ContactForm = ({ onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    emailjs.init(PUBLIC_KEY);
  }, []);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSending(true);
    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          name: 'RareML Website',
          from_name: 'RareML Website',
          email: RAREML_FROM_EMAIL,
          from_email: RAREML_FROM_EMAIL,
          reply_to: form.email,
          contact_name: form.name,
          contact_email: form.email,
          visitor_name: form.name,
          visitor_email: form.email,
          message: form.message,
          to_email: RAREML_FROM_EMAIL,
          subject: `New Voice AI contact request from ${form.name}`,
          summary: `${form.name} (${form.email}) sent a new contact request from the RareML Voice AI landing page.`,
          source: 'rareml-voice-ai-landing'
        },
        PUBLIC_KEY
      );

      setSubmitted(true);
      setForm({ name: '', email: '', message: '' });
    } catch (sendError) {
      const message = typeof sendError?.text === 'string' && sendError.text
        ? sendError.text
        : 'Sorry, something went wrong. Please try again later.';
      setError(message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(4, 10, 20, 0.72)',
        backdropFilter: 'blur(18px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        zIndex: 1000
      }}
    >
      <div
        style={{
          width: 'min(100%, 520px)',
          borderRadius: 30,
          background: 'linear-gradient(180deg, rgba(16, 28, 49, 0.96), rgba(8, 16, 29, 0.98))',
          border: '1px solid rgba(169, 183, 208, 0.18)',
          boxShadow: '0 32px 80px rgba(0, 0, 0, 0.42)',
          padding: 28,
          position: 'relative'
        }}
      >
        <button
          type="button"
          onClick={() => {
            onClose();
            setSubmitted(false);
            setError('');
            setSending(false);
          }}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: 18,
            right: 18,
            width: 42,
            height: 42,
            borderRadius: 999,
            border: '1px solid rgba(169, 183, 208, 0.18)',
            background: 'rgba(255,255,255,0.03)',
            color: '#f5f7fb',
            cursor: 'pointer',
            fontSize: '1.25rem'
          }}
        >
          ×
        </button>

        <div className="section-kicker" style={{ marginBottom: 18 }}>
          Contact RareML
        </div>
        <h2 className="display-title" style={{ fontSize: '1.85rem', margin: '0 0 12px' }}>
          Start your Voice AI journey
        </h2>
        <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 24px', fontSize: '0.98rem' }}>
          Share your use case and RareML will follow up.
        </p>

        {submitted ? (
          <div
            style={{
              borderRadius: 22,
              border: '1px solid rgba(144, 243, 191, 0.24)',
              background: 'rgba(144, 243, 191, 0.08)',
              color: '#dfffee',
              padding: '28px 20px',
              textAlign: 'center',
              lineHeight: 1.7
            }}
          >
            Thanks. Your request has been sent and RareML will get back to you soon.
          </div>
        ) : (
          <form onSubmit={handleFormSubmit}>
            <div style={{ display: 'grid', gap: 14 }}>
              <input
                type="text"
                name="name"
                placeholder="Your name"
                value={form.name}
                onChange={handleFormChange}
                required
                style={fieldStyle}
                disabled={sending}
              />
              <input
                type="email"
                name="email"
                placeholder="Work email"
                value={form.email}
                onChange={handleFormChange}
                required
                style={fieldStyle}
                disabled={sending}
              />
              <textarea
                name="message"
                placeholder="Tell us what kind of Voice AI workflow or customer experience you want to build"
                value={form.message}
                onChange={handleFormChange}
                required
                rows={5}
                style={{ ...fieldStyle, resize: 'vertical' }}
                disabled={sending}
              />
            </div>

            {error && (
              <div style={{ color: '#ff9c9c', marginTop: 14 }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="primary-btn"
              style={{ width: '100%', marginTop: 20, opacity: sending ? 0.7 : 1 }}
              disabled={sending}
            >
              {sending ? 'Sending...' : 'Send Request'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactForm;
